import { Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import type { CustomFieldValue } from '@vy/custom-fields-types';
import { isNotNullOrUndefined } from '@vy/utils';

import {
  findCustomFieldValue,
  getCustomFieldDisplayName,
} from '@vy/custom-fields-display';

function getCustomFieldValue(
  row: Record<string, unknown>,
  fieldName: string,
): string | number | boolean | null {
  const flattenedValue = row[fieldName];
  if (isNotNullOrUndefined(flattenedValue)) {
    return flattenedValue as string | number | boolean;
  }

  const customFieldValues = row.customFieldValues as CustomFieldValue[] | undefined;
  if (!customFieldValues) {
    return null;
  }

  return findCustomFieldValue(customFieldValues, fieldName);
}

function formatMongoValueForDisplay(value: unknown, placeholder = '--'): string {
  if (value === null || value === undefined) {
    return placeholder;
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    for (const key of ['$numberDouble', '$numberInt', '$numberLong'] as const) {
      if (key in obj && typeof obj[key] === 'string') return String(Number(obj[key]));
    }
    if ('$date' in obj && typeof obj['$date'] === 'string') return new Date(obj['$date']).toString();
    if ('$oid' in obj && typeof obj['$oid'] === 'string') return obj['$oid'] as string;
  }
  return String(value);
}

type RowWithFlattenedCustomFields = Record<string, unknown>;

export function createCustomFieldColumns<T extends RowWithFlattenedCustomFields>(
  fieldNames: string[],
): GridColDef<T>[] {
  return fieldNames.map((fieldName) => ({
    field: fieldName,
    headerName: getCustomFieldDisplayName(fieldName),
    description: getCustomFieldDisplayName(fieldName),
    minWidth: 150,
    sortable: false,
    customField: true,
    valueGetter: (_value, row) => getCustomFieldValue(row as Record<string, unknown>, fieldName),
    renderCell: (params) => {
      const rawValue =
        params.value ?? getCustomFieldValue(params.row as Record<string, unknown>, fieldName);
      const displayValue = formatMongoValueForDisplay(rawValue);

      return (
        <Typography
          variant="body2"
          sx={{
            color: 'black',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
          }}
        >
          {displayValue}
        </Typography>
      );
    },
  }));
}
